import React from "react";
import {
    FormGroup,
    Form,
    Input,
    Container,
    Label,
    Row,
    Col,
    Spinner
} from "reactstrap";
import 'react-toastify/dist/ReactToastify.css';
import StyledButton from "../../components/Buttons/StyledButton";
import { useState, useEffect } from 'react';
import { EmailRegex, ZipcodeRegex } from '../../constants/regex';
import { InitialAccountDataStorageKey } from '../../constants/localStorage';
import states from '../../constants/states';
import { useAuth } from "react-oidc-context";
import { PhoneNumberUtil } from "google-libphonenumber";
import Select from "react-select";
import { validateExistingEmailAddress } from '../../utilities/orgPanelApiCalls';
import { errorToast } from "../../constants/toast";
import { ToastMessage } from "../ToastMessage/ToastMessage";

const InitialAccountData = ({ submitHandler }) => {

    const phoneUtil = PhoneNumberUtil.getInstance();

    const [manageData, setData] = useState(() => {
        const manageData = JSON.parse(localStorage.getItem(InitialAccountDataStorageKey));

        return manageData || {};
    });

    const [phoneNumberIsValid, setPhoneNumberIsValid] = useState(false);
    const [emailIsValid, setEmailIsValid] = useState(true);
    const [emailExists, setEmailExists] = useState(false);
    const [zipcodeIsValid, setZipcodeIsValid] = useState(false);
    const [toast, SetToast] = useState();
    const [isLoading, setIsLoading] = useState(false);

    const makeToast = (message, type) => {
        SetToast({
            display: true,
            message: message,
            type: type
        });
    };

    const toastToggleCallback = () => {
        SetToast({
            display: false,
            message: ""
        });
    };

    const validateEmailMatch = (value, targetType) => {
        let isValid = false;
        //added check in both direction to validate on either input change
        try {
            if (targetType === true) {
                if (manageData.email.toLowerCase() === value.toLowerCase()) {
                    isValid = true;
                }
            }
            else if (targetType === false) {
                if (manageData.emailConfirmation.toLowerCase() === value.toLowerCase()) {
                    isValid = true;
                }
            }
        } catch { }
        setEmailIsValid(isValid);
    };

    const validatePhoneNumber = (phoneNumber) => {
        let isValid = false;
        try {
            const number = phoneUtil.parse("+1-"+phoneNumber);
            isValid = phoneUtil.isValidNumber(number);
        } catch { }
        setPhoneNumberIsValid(isValid);
    };

    const validateZip = (zipcode) => {
        const zipRegex = new RegExp(ZipcodeRegex);
        let isValid = false;
        if (zipRegex.test(zipcode)) {
            isValid = true;
        }
        setZipcodeIsValid(isValid);
    };

    const auth = useAuth();

    const checkIfEmailAlreadyExistsInDcgPortal = async (emailAddress) => {
        var result = await validateExistingEmailAddress(emailAddress);
        return result.data.emailAlreadyExistsInDcgPortal;
    };

    useEffect(() => {
        if (auth.isAuthenticated) {
            setData({
                ...manageData,
                firstName: auth.user.profile.given_name,
                lastName: auth.user.profile.family_name,
                email: auth.user.profile.email,
                emailConfirmation: auth.user.profile.email
            });
        }
    }, [auth]);

    useEffect(() => {
        if (manageData) {
            localStorage.setItem(InitialAccountDataStorageKey, JSON.stringify(manageData));
            validatePhoneNumber(manageData.phoneNumber);
            validateZip(manageData.zip);
        }

    }, [manageData, validatePhoneNumber]);

    const handleInputChange = (event) => {
        const target = event.target;
        const name = target.id;
        setData({ ...manageData, [name]: target.value })
    };

    const handlePhoneNumberInputChange = (event) => {
        validatePhoneNumber(event.target.value);
        handleInputChange(event);
    };

    const handleZipcodeChange = (event) => {
        validateZip(event.target.value);
        handleInputChange(event);
    };

    const handleEmailChange = (event) => {
        let targetType = event.target.id === "emailConfirmation";
        validateEmailMatch(event.target.value, targetType);
        handleInputChange(event);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        if (!phoneNumberIsValid) {
            const toastMsg = 'Please enter a valid phone number';
            makeToast(toastMsg, errorToast);
        }
        else if (!zipcodeIsValid) {
            const toastMsg = 'Please enter a valid postal code';
            makeToast(toastMsg, errorToast);
        }
        else if (!emailIsValid) {
            const toastMsg = 'Supplied email addresses do not match. Please verify your email address.';
            makeToast(toastMsg, errorToast);
        }
        else if (emailIsValid) {
            const emailAlreadyExistsInDcgPortal = await checkIfEmailAlreadyExistsInDcgPortal(event.target.email.defaultValue);
            setIsLoading(false);

            if(emailAlreadyExistsInDcgPortal){
                const toastMsg = 'The email address supplied is linked to another DCG portal. Please login and access the portal currently linked to this email address.';
                makeToast(toastMsg, errorToast);
            }
            else {
                submitHandler();
            }
        }
        else {
            submitHandler();
        }
        setIsLoading(false);
    };

    return (
        <>
            {toast?.display && (
                <ToastMessage Message={toast?.message} Type={toast?.type} ToggleCallback={toastToggleCallback}/>
            )}
            <h2 className="m-4 text-center">Portal Admin Information</h2>
            <Container>
                {manageData !== null &&
                    <Form onSubmit={handleSubmit}>
                        <>
                            <FormGroup row>
                                <Col sm={1}></Col>
                                <Label
                                    for="firstName"
                                    sm={4}
                                >
                                    First Name:
                                </Label>
                                <Col sm={6}>
                                    <Input
                                        id="firstName"
                                        required
                                        value={manageData?.firstName}
                                        onChange={handleInputChange}
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row>
                                <Col sm={1}></Col>
                                <Label
                                    for="lastName"
                                    sm={4}
                                >
                                    Last Name:
                                </Label>
                                <Col sm={6}>
                                    <Input
                                        id="lastName"
                                        required
                                        value={manageData?.lastName}
                                        onChange={handleInputChange}
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row>
                                <Col sm={1}></Col>
                                <Label
                                    for="email"
                                    sm={4}
                                >
                                    Email Address:
                                </Label>
                                <Col sm={6}>
                                    <Input
                                        pattern={EmailRegex}
                                        title="Please enter a valid email"
                                        type="email"
                                        id="email"
                                        required
                                        value={manageData?.email}
                                        readOnly={auth.isAuthenticated}
                                        onChange={handleEmailChange}

                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row>
                                <Col sm={1}></Col>
                                <Label
                                    for="emailConfirmation"
                                    sm={4}
                                >
                                    Email Confirmation:
                                </Label>
                                <Col sm={6}>
                                    <Input
                                        pattern={EmailRegex}
                                        title="Please confirm the supplied email"
                                        type="email"
                                        id="emailConfirmation"
                                        required
                                        value={manageData?.emailConfirmation}
                                        readOnly={auth.isAuthenticated}
                                        onChange={handleEmailChange}
                                        invalid={!emailIsValid}
                                    />
                                    {!emailIsValid && (<span className="validation-warning-message">** Please verify that email addresses match</span>)}
                                </Col>
                            </FormGroup>

                            <FormGroup row>
                                <Col sm={1}></Col>
                                <Label
                                    for="phoneNumber"
                                    sm={4}
                                >
                                    Phone Number:
                                </Label>
                                <Col sm={6}>
                                    <Input
                                        id="phoneNumber"
                                        required
                                        value={manageData?.phoneNumber}
                                        onChange={handlePhoneNumberInputChange}
                                        valid={phoneNumberIsValid}
                                        invalid={!phoneNumberIsValid}
                                    />
                                    {!phoneNumberIsValid && (<span className="validation-warning-message">** Please enter a valid phone number in XXX-XXX-XXXX format</span>) }
                                </Col>
                            </FormGroup>

                            <FormGroup row>
                                <Col sm={1}></Col>
                                <Label
                                    for="portalName"
                                    sm={4}
                                >
                                    Portal Name:
                                </Label>
                                <Col sm={6}>
                                    <Input
                                        id="portalName"
                                        required
                                        maxLength={30}
                                        value={manageData?.portalName}
                                        onChange={handleInputChange}
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row>
                                <Col sm={1}></Col>
                                <Label
                                    for="company"
                                    sm={4}
                                >
                                    Portal Owner (Organization Name):
                                </Label>
                                <Col sm={6}>
                                    <Input
                                        id="company"
                                        required
                                        value={manageData?.company}
                                        maxLength={100}
                                        onChange={handleInputChange}
                                    />
                                </Col>
                            </FormGroup>

                           

                            <FormGroup >
                                <Row className="mb-1">
                                <Col sm={1}></Col>
                                    <Label
                                        for="address1"
                                        sm={4}
                                    >
                                        Address:
                                    </Label>
                                    <Col sm={6}>
                                        <Input
                                            id="address"
                                            required
                                            value={manageData?.address}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                </Row>

                                <Row className="mb-1">
                                <Col sm={1}></Col>
                                    <Label
                                        for="address2"
                                        sm={4}
                                    >
                                        Address 2:
                                    </Label>
                                    <Col sm={6}>
                                        <Input
                                            id="address2"
                                            placeholder="Optional"
                                            value={manageData?.address2}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                </Row>

                                <Row className="mb-1">
                                <Col sm={1}></Col>
                                    <Label
                                        for="city"
                                        sm={4}
                                    >
                                        City:
                                    </Label>
                                    <Col sm={6}>
                                        <Input
                                            required
                                            id="city"
                                            maxLength={30}
                                            value={manageData?.city}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                </Row>

                                <Row className="mb-1">
                                <Col sm={1}></Col>
                                    <Label
                                        sm={4}
                                    >
                                        State:
                                    </Label>
                                    <Col sm={6}>
                                        <Select
                                            id="state"
                                            name="state"
                                            onChange={opt => handleInputChange({
                                                target: {
                                                    id: 'state',
                                                    value: opt.value
                                                }
                                            })}
                                            required
                                            className="react-select"
                                            defaultValue={states.find(opt => opt.value == manageData?.state)}

                                            options={states}
                                        />
                                    </Col>
                                </Row>

                                <Row className="mb-1">
                                <Col sm={1}></Col>
                                    <Label

                                        for="zip"
                                        sm={4}
                                    >
                                        Postal Code:
                                    </Label>
                                    <Col sm={6}>
                                        <Input
                                            id="zip"
                                            required
                                            value={manageData?.zip}
                                            type="number"
                                            onChange={handleZipcodeChange}
                                            valid={zipcodeIsValid}
                                            invalid={!zipcodeIsValid}
                                        />
                                        {!zipcodeIsValid && (<span className="validation-warning-message">** Please enter a 5 digit postal code - XXXXX</span>)}
                                    </Col>
                                </Row>

                            </FormGroup>

                            <div>
                                <Row>
                                    <Col sm={12} className="text-center" >
                                        <StyledButton 
                                            className="buttom-btn continue-btn" 
                                            width="120px"
                                            height = "27px"
                                            data-testid="initial-account-next-btn" 
                                            color="success" 
                                            outline
                                            text={ isLoading ? <Spinner size="sm"/> : "Continue" }/>
                                    </Col>
                                </Row>
                            </div>
                        </>
                    </Form>
                }
            </Container>
        </>
    );
}

export default InitialAccountData;